import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createOrderDayPos,
  listOrderDay,
  runNightly,
  type OrderDayRow,
} from '../lib/queries.js';
import { Badge } from '../ui/Badge.js';
import { fmtInt } from '../lib/format.js';
import { useAuth } from '../auth/context.js';
import { resolveSkuDisplay } from '../lib/skuParser.js';
import { formatCopyText } from '../lib/copyFormat.js';

interface RowState {
  checked: boolean;
  qty: number;          // editable (defaults to suggested_qty)
}

const NO_SUPPLIER_KEY = '__no_supplier__';

export default function OrderDay() {
  const { me } = useAuth();
  const [rows, setRows] = useState<OrderDayRow[]>([]);
  const [state, setState] = useState<Record<string, RowState>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [done, setDone] = useState<{ count: number; refs: string[] } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    setDone(null);
    try {
      const r = await listOrderDay();
      setRows(r.items);
      // default: check rows that have a supplier; copy suggested into qty
      const init: Record<string, RowState> = {};
      for (const row of r.items) {
        init[row.sku] = {
          checked: row.supplier_id !== null && row.suggested_qty > 0,
          qty: row.suggested_qty,
        };
      }
      setState(init);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'load failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const refresh = async () => {
    if (me?.role !== 'super_admin') {
      await load();
      return;
    }
    setRefreshing(true);
    setError(null);
    try {
      await runNightly();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  // Group by supplier (null → "no supplier" bucket)
  const groups = useMemo(() => {
    const map = new Map<string, { name: string | null; supplier_id: number | null; rows: OrderDayRow[] }>();
    for (const r of rows) {
      const key = r.supplier_id === null ? NO_SUPPLIER_KEY : String(r.supplier_id);
      const existing = map.get(key);
      if (existing) {
        existing.rows.push(r);
      } else {
        map.set(key, { name: r.supplier_name, supplier_id: r.supplier_id, rows: [r] });
      }
    }
    return Array.from(map.values());
  }, [rows]);

  const totalPicked = useMemo(() => {
    let n = 0;
    for (const r of rows) if (state[r.sku]?.checked) n += 1;
    return n;
  }, [rows, state]);

  const totalQty = useMemo(() => {
    let q = 0;
    for (const r of rows) if (state[r.sku]?.checked) q += state[r.sku]!.qty;
    return q;
  }, [rows, state]);

  const setRow = (sku: string, patch: Partial<RowState>) =>
    setState((p) => ({ ...p, [sku]: { ...p[sku]!, ...patch } }));

  const toggleGroup = (groupKey: string, checked: boolean) => {
    const group = groups.find((g) =>
      String(g.supplier_id ?? NO_SUPPLIER_KEY) === groupKey
    );
    if (!group) return;
    const patch: Record<string, RowState> = { ...state };
    for (const r of group.rows) {
      patch[r.sku] = { ...patch[r.sku]!, checked: checked && r.supplier_id !== null };
    }
    setState(patch);
  };

  const copyGroup = (group: typeof groups[number]) => {
    const text = formatCopyText(
      group.rows
        .filter((r) => state[r.sku]?.checked)
        .map((r) => ({
          sku: r.sku,
          brand: r.brand,
          model: r.model,
          size: r.size,
          qty: state[r.sku]!.qty,
        }))
    );
    if (!text) return;
    void navigator.clipboard.writeText(text);
  };

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const items = rows
        .filter((r) => state[r.sku]?.checked && r.supplier_id !== null)
        .map((r) => ({
          sku: r.sku,
          qty: state[r.sku]!.qty,
          supplier_id: r.supplier_id!,
        }));
      if (items.length === 0) {
        setError('ยังไม่ได้เลือกรายการที่จะสร้าง PO');
        setSubmitting(false);
        return;
      }
      const result = await createOrderDayPos(items);
      setDone({
        count: result.created.length,
        refs: result.created.map((c) => c.po_ref ?? `#${c.po_id}`),
      });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-sm text-slate-500">กำลังโหลด...</div>;

  const today = new Intl.DateTimeFormat('th-TH', { dateStyle: 'full' }).format(new Date());

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">รายการสั่งยางวันนี้</h1>
          <p className="mt-1 text-sm text-slate-500">{today}</p>
          <p className="mt-0.5 text-xs text-slate-400">
            นับเฉพาะยาง DOT ปีปัจจุบัน — ของ DOT ปีเก่าไม่ถูกนับว่าครบ min/max
          </p>
        </div>
        <button
          onClick={refresh}
          disabled={refreshing || loading}
          title="รัน run_nightly แล้วโหลดข้อมูลใหม่"
          className="shrink-0 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-60"
        >
          {refreshing ? '⏳' : '🔄'} รีเฟรช
        </button>
      </div>

      {done && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
          ✅ สร้าง PO {done.count} ใบแล้ว: {done.refs.join(', ')}
          <Link to="/pos" className="ml-2 underline">
            ไปดูใน PO list
          </Link>
        </div>
      )}

      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      {rows.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center">
          <div className="text-4xl">🎉</div>
          <p className="mt-2 text-sm text-slate-600">
            ไม่มีอะไรต้องสั่งวันนี้ — ยาง DOT ปีนี้ของทุก SKU เกิน min ทั้งหมด
          </p>
          <p className="mt-1 text-xs text-slate-400">
            (รวมยางที่กำลังเดินทางมาด้วย)
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-xl border border-slate-200 bg-white p-3 text-sm">
            <span className="font-semibold">{rows.length}</span> SKU ใต้ min
            {' · '}
            <span className="font-semibold">{groups.length}</span> supplier
            {' · '}
            <span className="font-semibold">{totalPicked}</span> เลือกแล้ว
            (รวม <span className="font-semibold">{fmtInt(totalQty)}</span> เส้น)
          </div>

          {groups.map((g) => {
            const groupKey = String(g.supplier_id ?? NO_SUPPLIER_KEY);
            const allChecked = g.rows.every((r) => state[r.sku]?.checked);
            const someChecked = g.rows.some((r) => state[r.sku]?.checked);
            const noSupplier = g.supplier_id === null;
            return (
              <section key={groupKey} className="rounded-xl border border-slate-200 bg-white">
                <header className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
                  <div className="flex items-center gap-3">
                    {!noSupplier && (
                      <input
                        type="checkbox"
                        checked={allChecked}
                        ref={(el) => {
                          if (el) el.indeterminate = !allChecked && someChecked;
                        }}
                        onChange={(e) => toggleGroup(groupKey, e.target.checked)}
                      />
                    )}
                    <div>
                      <h2 className="font-semibold">
                        {g.name ?? '⚠️ ยังไม่ระบุ supplier'}
                      </h2>
                      <div className="text-xs text-slate-500">
                        {g.rows.length} SKU
                      </div>
                    </div>
                  </div>
                  {!noSupplier && (
                    <button
                      onClick={() => copyGroup(g)}
                      className="rounded-md border border-slate-200 px-3 py-1 text-xs hover:bg-slate-50"
                    >
                      📋 คัดลอกข้อความ
                    </button>
                  )}
                </header>
                <ul className="divide-y divide-slate-100">
                  {g.rows.map((r) => {
                    const s = state[r.sku] ?? { checked: false, qty: r.suggested_qty };
                    const dr = resolveSkuDisplay(r.sku, r.brand, r.model, r.size);
        const display = [dr.brand, dr.model, dr.size].filter(Boolean).join(' ');
                    return (
                      <li key={r.sku} className="flex flex-wrap items-center gap-3 px-4 py-3">
                        <input
                          type="checkbox"
                          checked={s.checked}
                          disabled={noSupplier}
                          onChange={(e) => setRow(r.sku, { checked: e.target.checked })}
                        />
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">
                            {display || <span className="font-mono text-xs">{r.sku}</span>}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-500">
                            <Link to={`/stock/${encodeURIComponent(r.sku)}`} className="font-mono hover:underline">
                              {r.sku}
                            </Link>
                            {' · '}
                            เหลือ (DOT ปีนี้){' '}
                            <span className="font-semibold">{r.effective_cy_total}</span> /
                            min <span className="text-slate-600">{r.min_total}</span> /
                            max <span className="text-slate-600">{r.target_total}</span>
                            {r.incoming_po_total > 0 && (
                              <> · <Badge tone="info">กำลังมา {r.incoming_po_total}</Badge></>
                            )}
                            {r.velocity_total > 0 && (
                              <> · ขาย {r.velocity_total.toFixed(1)}/วัน</>
                            )}
                            {r.days_of_cover !== null && r.days_of_cover < 7 && (
                              <> · <Badge tone={r.days_of_cover < 3 ? 'danger' : 'warn'}>
                                เหลือ {r.days_of_cover.toFixed(0)} วัน
                              </Badge></>
                            )}
                          </div>
                          {r.on_hand_old_total > 0 && (
                            <div className="mt-0.5 text-xs text-amber-700">
                              📦 มี DOT ปีเก่าค้าง {r.on_hand_old_total} เส้น — ไม่นับเป็นของที่ครบ
                              min (รวมทุกปี {r.effective_total})
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min={1}
                            value={s.qty}
                            disabled={noSupplier}
                            onChange={(e) => setRow(r.sku, { qty: Number(e.target.value) })}
                            className="w-20 rounded-md border border-slate-200 px-2 py-1 text-right text-sm disabled:bg-slate-50"
                          />
                          <span className="text-xs text-slate-500">เส้น</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {noSupplier && (
                  <div className="border-t border-slate-100 bg-amber-50 px-4 py-2 text-xs text-amber-900">
                    💡 SKU ในกลุ่มนี้ยังไม่ระบุ supplier — เปิดหน้า SKU แล้วตั้งค่า "Supplier หลัก"
                  </div>
                )}
              </section>
            );
          })}

          {me?.role === 'super_admin' && (
            <div className="sticky bottom-0 -mx-4 border-t border-slate-200 bg-white/90 px-4 py-3 backdrop-blur sm:rounded-xl sm:border sm:bg-white">
              <button
                onClick={submit}
                disabled={submitting || totalPicked === 0}
                className="w-full rounded-md bg-slate-900 px-4 py-3 text-sm font-semibold text-white disabled:opacity-60"
              >
                {submitting
                  ? 'กำลังบันทึก...'
                  : `บันทึกเป็น PO (${totalPicked} SKU · ${fmtInt(totalQty)} เส้น)`}
              </button>
              <p className="mt-1 text-center text-xs text-slate-500">
                ระบบจะสร้าง PO 1 ใบต่อ supplier โดยอัตโนมัติ
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
